<template>
  <div>
    <action-alert
      :close-method="closeAlert"
      :type="alert.type"
      :icon="alert.icon"
      :is-visible="alert.isVisible"
    >
      {{ alert.text }}
    </action-alert>
    <div class="container-fluid">
      <Breadcrumb :values="{ Home: '/', 'Open Ended Question Search': '' }"></Breadcrumb>
      <div class="animated fadeIn">
        <div class="card search-filters-card">
          <div class="card-body search-filters-card-body">
            <div class="description-wrapper">
              <span class="description-header">Open Ended Question Search</span>
              <span class="description-copy">
                Browse non-numeric free-text responses joined with question text and GCP text-file coverage.
              </span>
            </div>
            <form class="analyst-form" @submit.prevent="fetchRows">
              <div class="row w-100">
                <div class="col-12 col-xl-3 d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Question text</label>
                    <el-input
                      v-model="filters.questionText"
                      clearable
                      placeholder="Search question_text"
                    ></el-input>
                  </div>
                </div>
                <div class="col-12 col-xl-3 d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Name</label>
                    <el-input
                      v-model="filters.name"
                      clearable
                      placeholder="Search name"
                    ></el-input>
                  </div>
                </div>
                <div class="col-12 col-xl-3 d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>Response</label>
                    <el-input
                      v-model="filters.response"
                      clearable
                      placeholder="Search response"
                    ></el-input>
                  </div>
                </div>
                <div class="col-12 col-xl-3 d-flex flex-column align-items-center">
                  <div class="input-wrapper">
                    <label>GCP match status</label>
                    <el-select v-model="filters.gcpMatchStatus" placeholder="All statuses" clearable>
                      <el-option
                        v-for="option in statusOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                      ></el-option>
                    </el-select>
                  </div>
                </div>
              </div>
              <div class="search-actions">
                <button class="search-button-alt btn" type="button" @click="clearFilters">
                  Clear
                </button>
                <button class="search-button btn" type="submit" :disabled="isLoading">
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        <div class="card results-card mt-4">
          <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
            <span>Open Ended Question Results</span>
            <span v-if="hasLoaded && !isLoading" class="results-count">
              {{ rows.length }} rows loaded. Showing up to {{ resultLimit }} rows.
            </span>
          </div>
          <div class="card-body results-body">
            <Loader :show="isLoading" :fixed="false"></Loader>

            <div v-if="hasLoaded && rows.length" class="table-wrapper">
              <VClientTable
                class="hide-filters analyst-table"
                :data="rows"
                :columns="columns"
                :options="tableOptions"
              >
                <template #study_id="props">
                  <router-link
                    v-if="props.row.study_id"
                    :to="{ name: 'study-details', params: { id: props.row.study_id } }"
                    class="study-link"
                    target="_blank"
                  >
                    Link
                  </router-link>
                  <span v-else>-</span>
                </template>
                <template #question_text="props">
                  <span class="text-cell">{{ props.row.question_text || '-' }}</span>
                </template>
                <template #prod_code="props">
                  <span class="text-cell">
                    {{ typeof props.row.prod_code === 'string' && props.row.prod_code.trim() ? props.row.prod_code.trim() : '-' }}
                  </span>
                </template>
                <template #name="props">
                  <span class="text-cell">
                    {{ typeof props.row.name === 'string' && props.row.name.trim() ? props.row.name.trim() : '-' }}
                  </span>
                </template>
                <template #response="props">
                  <span class="text-cell">{{ props.row.response || '-' }}</span>
                </template>
                <template #file_type_breakdown="props">
                  <span class="text-cell">{{ props.row.file_type_breakdown || '-' }}</span>
                </template>
                <template #gcp_match_status="props">
                  <span
                    class="badge status-badge"
                    :class="props.row.gcp_match_status === 'matched_gcp_text' ? 'badge-success' : 'badge-secondary'"
                  >
                    {{ props.row.gcp_match_status }}
                  </span>
                </template>
              </VClientTable>
            </div>
            <div v-else-if="!isLoading" class="empty-state">
              <i>{{ hasLoaded ? 'No data' : 'Set at least one filter and run search.' }}</i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionAlert from '../components/shared/ActionAlert'
import Breadcrumb from '../components/shared/Breadcrumb'
import Loader from '../components/shared/Loader'
import VClientTable from '../components/shared/VClientTable'
import http from '../services/http'

export default {
  name: 'OpenEndedQuestionSearch',
  components: {
    ActionAlert,
    Breadcrumb,
    Loader,
    VClientTable
  },
  data () {
    return {
      filters: {
        questionText: '',
        name: '',
        response: '',
        gcpMatchStatus: ''
      },
      statusOptions: [
        { label: 'Matched GCP Text', value: 'matched_gcp_text' },
        { label: 'No GCP Text Match', value: 'no_gcp_text_match' }
      ],
      rows: [],
      resultLimit: 100,
      hasLoaded: false,
      isLoading: false,
      alert: {
        type: '',
        icon: '',
        text: '',
        isVisible: false
      }
    }
  },
  computed: {
    columns () {
      return [
        { field: 'qid', label: 'QID', sortable: true },
        { field: 'question_text', label: 'Question Text', sortable: true },
        { field: 'prod_code', label: 'Prod Code', sortable: true },
        { field: 'name', label: 'Name', sortable: true },
        { field: 'response', label: 'Response', sortable: true },
        { field: 'total_text_files', label: 'Total Text Files', sortable: true },
        { field: 'distinct_file_types', label: 'Distinct File Types', sortable: true },
        { field: 'file_type_breakdown', label: 'File Type Breakdown', sortable: false },
        { field: 'gcp_match_status', label: 'GCP Match Status', sortable: true },
        { field: 'study_id', label: 'Study Details', sortable: true }
      ]
    },
    hasActiveFilters () {
      return [
        this.filters.questionText,
        this.filters.name,
        this.filters.response,
        this.filters.gcpMatchStatus
      ].some(value => value && value.toString().trim() !== '')
    },
    tableOptions () {
      return {
        perPage: 10,
        perPageDropdown: [10, 25, 50, 100],
        sortable: ['qid', 'question_text', 'prod_code', 'name', 'study_id', 'response', 'total_text_files', 'distinct_file_types', 'gcp_match_status']
      }
    }
  },
  methods: {
    closeAlert () {
      this.alert.isVisible = false
    },
    toggleAlert (type, icon, text) {
      this.alert = {
        type,
        icon,
        text,
        isVisible: true
      }
    },
    getErrorMessage (error) {
      if (typeof error?.response?.data === 'string') {
        const text = error.response.data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        if (text) {
          return text
        }
      }
      if (typeof error?.response?.data?.message === 'string') {
        return error.response.data.message
      }
      if (error?.response?.status) {
        return `Request failed with status ${error.response.status}.`
      }
      return 'Unable to load open ended question results.'
    },
    async fetchRows () {
      if (!this.hasActiveFilters) {
        this.rows = []
        this.hasLoaded = false
        this.toggleAlert('danger', 'exclamation-circle', 'Set at least one filter before searching.')
        return
      }

      this.closeAlert()
      this.isLoading = true

      try {
        const response = await http.post('/smartsearch/ai_analyst_rows', {
          question_text: this.filters.questionText,
          name: this.filters.name,
          response: this.filters.response,
          gcp_match_status: this.filters.gcpMatchStatus,
          limit: this.resultLimit
        })
        this.rows = response.data.results || []
        this.resultLimit = response.data.limit || this.resultLimit
        this.hasLoaded = true
        if (this.rows.length && !('prod_code' in this.rows[0]) && !('name' in this.rows[0])) {
          this.toggleAlert(
            'danger',
            'exclamation-circle',
            'API response is missing prod_code and name. Restart the backend service so the updated query is loaded.'
          )
        }
      } catch (error) {
        this.rows = []
        this.hasLoaded = true
        this.toggleAlert('danger', 'exclamation-circle', this.getErrorMessage(error))
      } finally {
        this.isLoading = false
      }
    },
    clearFilters () {
      this.filters = {
        questionText: '',
        name: '',
        response: '',
        gcpMatchStatus: ''
      }
      this.rows = []
      this.hasLoaded = false
      this.closeAlert()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/scss/variables";

.search-filters-card {
  margin-bottom: 0;
  box-shadow: 0 5px 7px #999999;
  background-color: $light-grey;
  border: 0;

  .search-filters-card-body {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 38px 60px;
  }
}

.description-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
}

.description-header {
  color: $default-focus;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
}

.description-copy {
  color: $default-focus;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 24px;
  margin-top: 4px;
}

.analyst-form {
  width: 100%;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  color: $input-label;
  margin-bottom: 2px;
  margin-left: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 17px;
  letter-spacing: 0;
}

.search-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
}

.search-button {
  width: 100%;
  max-width: 230px;
  height: 48px;
  background-color: $blueberry;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;
}

.search-button:hover:enabled {
  background-color: #9256D9;
}

.search-button-alt {
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  width: 100%;
  max-width: 230px;
  height: 48px;
  background-color: #F8F8F8;
  border-radius: 4px;
  color: $button-text;
  text-transform: uppercase;
  transition: all 0.2s linear;
  margin-right: 20px;
}

.search-button-alt:hover {
  border-color: #dedede;
  background-color: white;
}

.results-card {
  border: 0;
  box-shadow: 0 5px 7px #999999;
}

.results-body {
  position: relative;
  min-height: 240px;
}

.results-count {
  color: $default-focus;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.empty-state {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: $default-focus;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
}

.table-wrapper {
  width: 100%;
}

.text-cell {
  white-space: normal;
  line-height: 1.45;
}

.study-link {
  color: $home-card;
  font-weight: 600;
}

.status-badge {
  text-transform: none;
}

.analyst-table :deep(.vgt-wrap) {
  overflow-x: auto;
}

.analyst-table :deep(table.vgt-table td),
.analyst-table :deep(table.vgt-table th) {
  vertical-align: top;
}

@media screen and (max-width: $break-lg - 1) {
  .search-filters-card .search-filters-card-body {
    padding: 24px 18px;
  }

  .search-actions {
    flex-direction: column;
    gap: 12px;
    margin-top: 18px;
  }

  .search-button-alt {
    margin-right: 0;
  }
}
</style>
